import { makeStyles } from "@mui/styles";
import { Section } from "components/disw";
import DatePicker from "components/disw/DatePicker";
import { FormikErrors } from "formik";
import React from "react";
import { MonthRangePicker } from "../../../components/disw/MonthRangePicker";
import { ServiceProductCreatable, ServiceProductPeriodInput } from "./types";

interface DurationSectionProps {
  singlePeriod: boolean;
  onSinglePeriodChange: (val: boolean) => void;
  values: Partial<ServiceProductCreatable> & {
    startMonth?: Date;
    endMonth?: Date;
  };
  errors: FormikErrors<ServiceProductCreatable> & {
    startMonth?: string;
    endMonth?: string;
  };
  setFieldValue: (
    field: string,
    value: unknown,
    shouldValidate?: boolean
  ) => void;
  getMonthlyPeriods: (start: Date, end: Date) => ServiceProductPeriodInput[];
}

const useStyles = makeStyles(theme => ({
  flex: {
    display: "flex",
    flexDirection: "column"
  },
  segmentedButtons: {
    display: "flex",
    flexDirection: "row",
    height: 32,
    width: "100%",
    gap: 0,
    marginBottom: theme.spacing(2)
  },
  segmentedButtonBase: {
    backgroundColor: "#ffffff",
    border: "1px solid  #00000026",
    padding: theme.spacing(1),
    fontFamily: "Segoe UI !important",
    color: "#1E1E1E",
    fontWeight: "400 ",
    fontSize: "12px !important",
    lineHeight: "16px",
    letterSpacing: 0,
    textAlign: "center",
    flex: 1,
    "&:first-child": {
      borderTopLeftRadius: 4,
      borderBottomLeftRadius: 4
    },
    "&:last-child": {
      borderTopRightRadius: 4,
      borderBottomRightRadius: 4
    }
  },
  segmentedButtonAccent: {
    backgroundColor: "#005F87",
    border: "1px solid  #00000026",
    padding: theme.spacing(1),
    fontFamily: "Segoe UI !important",
    color: "#ffffff",
    fontWeight: "400 ",
    fontSize: "12px !important",
    lineHeight: "16px",
    letterSpacing: 0,
    textAlign: "center",
    flex: 1,
    "&:first-child": {
      borderTopLeftRadius: 4,
      borderBottomLeftRadius: 4,
      borderTopRightRadius: 0,
      borderBottomRightRadius: 0
    },
    "&:last-child": {
      borderTopLeftRadius: 0,
      borderBottomLeftRadius: 0,
      borderTopRightRadius: 4,
      borderBottomRightRadius: 4
    }
  }
}));

const PeriodTypeSelector: React.FC<{
  singlePeriod: boolean;
  onChange: (val: boolean) => void;
}> = ({ singlePeriod, onChange }) => {
  const classes = useStyles();
  return (
    <div className={classes.segmentedButtons}>
      <div
        className={
          singlePeriod
            ? classes.segmentedButtonAccent
            : classes.segmentedButtonBase
        }
        onClick={() => onChange(true)}
        style={{ cursor: "pointer" }}
      >
        Single Period
      </div>
      <div
        className={
          singlePeriod
            ? classes.segmentedButtonBase
            : classes.segmentedButtonAccent
        }
        onClick={() => onChange(false)}
        style={{ cursor: "pointer" }}
      >
        Monthly recurring
      </div>
    </div>
  );
};

const DurationSection: React.FC<DurationSectionProps> = ({
  singlePeriod,
  onSinglePeriodChange,
  values,
  errors,
  setFieldValue,
  getMonthlyPeriods
}) => {
  const classes = useStyles();
  const startMonthValue = values.startMonth;
  const endMonthValue = values.endMonth;
  return (
    <Section name="Duration">
      <div className={classes.flex}>
        <PeriodTypeSelector
          singlePeriod={singlePeriod}
          onChange={onSinglePeriodChange}
        />
        {singlePeriod ? (
          <DatePicker
            label="Start Date - End Date"
            startDate={values.periods?.[0]?.startDate}
            endDate={values.periods?.[0]?.endDate}
            handleDateRangeChange={e => {
              setFieldValue("periods[0].startDate", e[0]);
              setFieldValue("periods[0].endDate", e[1]);
            }}
            isError={Boolean(errors.periods)}
            errorText={
              typeof errors.periods === "string"
                ? errors.periods
                : JSON.stringify(errors.periods)
            }
          />
        ) : (
          <MonthRangePicker
            period={{
              startMonth: startMonthValue,
              endMonth: endMonthValue
            }}
            onRangeChanged={({ startMonth, endMonth }) => {
              setFieldValue("startMonth", startMonth);
              setFieldValue("endMonth", endMonth);

              if (startMonth && endMonth) {
                const periods = getMonthlyPeriods(startMonth, endMonth);
                setFieldValue("periods", periods);
              } else {
                setFieldValue("periods", []);
              }
            }}
            isErrorInStartMonthPicker={Boolean(errors.startMonth)}
            isErrorInEndMonthPicker={Boolean(errors.endMonth)}
            startMonthErrorText={errors.startMonth}
            endMonthErrorText={errors.endMonth}
          />
        )}
      </div>
    </Section>
  );
};

export default DurationSection;
